import {
  Editor,
  Location,
  Node,
  Element,
  Range,
  Descendant,
  Text,
} from 'slate';
import { TextColourKey } from '../plugins/richtext/colours';
import { AulaElement, AulaText, RichTextMark } from '../types';

export const getCurrentNode = (editor: Editor): Node | null =>
  editor.selection
    ? Editor.node(editor, editor.selection as Location, { depth: 1 })[0]
    : null;

export const getCurrentFragment = (editor: Editor): Array<Element> => {
  const fragment = editor.getFragment()[0] as Element;
  return (fragment?.children as []) || [];
};

export const getCurrentNodeType = (editor: Editor): string | null => {
  const node = getCurrentNode(editor) as Descendant;
  return Element.isElement(node) && typeof node.type === 'string'
    ? node.type
    : null;
};

export const hasRichTextMark = (editor: Editor, richTextMark: RichTextMark) => {
  const [match] = Editor.nodes(editor, {
    match: (n) => Text.isText(n) && n[richTextMark] === true,
    mode: 'all',
  });
  return !!match;
};

export const getTextColour = (editor: Editor): TextColourKey | null => {
  const colours = [
    ...new Set(
      editor
        .getFragment()
        .flatMap((node) => (node as AulaElement).children || [])
        .filter((node) => {
          const isNonEmptyTextNode = Text.isText(node) && !!node.text;
          const { type, children } = node as AulaElement;
          const isInlineLinkWithText =
            type === 'inlineLink' &&
            children &&
            children.length > 0 &&
            !!(children[0] as AulaText).text;
          return isNonEmptyTextNode || isInlineLinkWithText;
        })
        .map((node) => {
          if (Text.isText(node)) return node.c;
          return node.children[0].c;
        }),
    ),
  ] as TextColourKey[];

  const selectedNodeHasNoColourMark = colours[0] === undefined;
  if (colours.length === 1 && selectedNodeHasNoColourMark) {
    return 'Default';
  }

  const hasMultipleColours = colours.length > 1;

  return hasMultipleColours ? null : colours[0];
};

export const hasSelection = (editor: Editor) =>
  editor.selection &&
  !Range.isCollapsed(editor.selection) &&
  Editor.string(editor, editor.selection) !== '';

export const isCurrentSelectionCollapsed = (editor: Editor) =>
  Range.isRange(editor.selection) && Range.isCollapsed(editor.selection);

export const selectionIncludesElementOfType = (
  editor: Editor,
  ...types: string[]
) =>
  !!Array.from(
    Editor.nodes(editor, {
      mode: 'all',
      match: (node) =>
        Element.isElement(node) && types.some((type) => node.type === type),
    }),
  ).length;

export const isEmptyElement = (element: Node) => {
  if (!Element.isElement(element)) {
    return false;
  }

  const { children } = element;

  if (children.length === 0) {
    return true;
  }

  const [first] = children;

  return (
    children.length === 1 &&
    Text.isText(first) &&
    first.text.replace(/\n|\r/g, '') === '' &&
    element.entity == null
  );
};
